import React from 'react';
import { Stethoscope, ArrowRight, CalendarDays, Users, ClipboardList, HeartPulse, CheckCircle, MessageSquare, FileQuestion, PhoneCall, Mail, Laptop, Smartphone, Shield, FileSearch, ActivitySquare, BookOpen, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { ProductLayout } from '../../components/layouts/ProductLayout';
import { AnimatedSection } from '../../components/AnimatedSection';
import { Wave } from '../../components/Wave';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { getPageSEO } from '../../data/seoData';

export const GalenosWeb = () => {
  // SEO Configuration
  useSEO(getPageSEO('galenos'));

  const productConfig = {
    name: 'Galenos Web',
    color: 'red',
    gradientFrom: 'from-red-600',
    gradientTo: 'to-red-700',
    description: 'Sistema integral para la gestión de clínicas y consultorios médicos que optimiza agendas, expedientes clínicos y procesos administrativos en una plataforma segura',
    links: [{
      label: 'Inicio',
      path: '/galenos'
    }, {
      label: 'Características',
      path: '/galenos#caracteristicas'
    }, {
      label: 'Precios',
      path: '/galenos#precios'
    }, {
      label: 'Soporte',
      path: '/galenos#soporte'
    }]
  };
  const features = [{
    icon: <CalendarDays className="w-8 h-8 text-red-600" />,
    title: 'Gestión de Citas',
    description: 'Sistema inteligente de agendamiento con recordatorios automáticos y confirmación de asistencia para optimizar tu tiempo'
  }, {
    icon: <Users className="w-8 h-8 text-red-600" />,
    title: 'Historias Clínicas',
    description: 'Registro digital completo del historial médico con búsqueda avanzada y acceso seguro desde cualquier dispositivo'
  }, {
    icon: <ClipboardList className="w-8 h-8 text-red-600" />,
    title: 'Informes Médicos',
    description: 'Generación automática de informes y documentación médica con plantillas personalizables por especialidad'
  }, {
    icon: <HeartPulse className="w-8 h-8 text-red-600" />,
    title: 'Seguimiento Pacientes',
    description: 'Monitoreo continuo de la evolución de tratamientos con alertas y recordatorios para citas de control'
  }, {
    icon: <ActivitySquare className="w-8 h-8 text-red-600" />,
    title: 'Signos Vitales',
    description: 'Registro y seguimiento de signos vitales con gráficos de tendencias y alertas por valores anormales'
  }, {
    icon: <FileSearch className="w-8 h-8 text-red-600" />,
    title: 'Gestión de Estudios',
    description: 'Organización de estudios de laboratorio e imágenes con visualizador integrado y comparación histórica'
  }];
  const pricingPlans = [{
    name: 'Consultorios',
    price: '$59',
    period: 'mes',
    description: 'Ideal para médicos independientes',
    features: ['Hasta 200 pacientes', '1 profesional', '1 asistente', 'Agenda básica', 'Historias clínicas', 'Soporte por email'],
    cta: 'Comenzar prueba gratuita',
    highlighted: false
  }, {
    name: 'Clínicas',
    price: '$129',
    period: 'mes',
    description: 'Perfecto para clínicas medianas',
    features: ['Hasta 2,000 pacientes', '5 profesionales', '3 asistentes', 'Agenda avanzada', 'Historias clínicas completas', 'Facturación integrada', 'Soporte prioritario'],
    cta: 'Comenzar prueba gratuita',
    highlighted: true
  }, {
    name: 'Hospitales',
    price: '$299',
    period: 'mes',
    description: 'Solución completa para centros médicos',
    features: ['Pacientes ilimitados', 'Profesionales ilimitados', 'Personal ilimitado', 'Módulos especializados', 'Integraciones con equipos médicos', 'API completa', 'Soporte dedicado 24/7'],
    cta: 'Contactar ventas',
    highlighted: false
  }];
  const supportOptions = [{
    icon: <MessageSquare className="w-10 h-10 text-red-600" />,
    title: 'Asistencia en línea',
    description: 'Chat en vivo con especialistas médicos y técnicos para resolver tus dudas en tiempo real.'
  }, {
    icon: <BookOpen className="w-10 h-10 text-red-600" />,
    title: 'Manuales clínicos',
    description: 'Biblioteca completa de guías especializadas por área médica y tutoriales interactivos.'
  }, {
    icon: <PhoneCall className="w-10 h-10 text-red-600" />,
    title: 'Soporte telefónico',
    description: 'Línea directa con nuestro equipo de soporte técnico especializado en sistemas médicos.'
  }, {
    icon: <Shield className="w-10 h-10 text-red-600" />,
    title: 'Asistencia HIPAA',
    description: 'Asesoramiento sobre cumplimiento normativo y protección de datos sensibles de pacientes.'
  }];
  return <ProductLayout product={productConfig}>
      <section className="bg-gradient-to-b from-red-800 via-red-700 to-red-600">
        <Wave color="#f9fafb" className="bg-red-600" pattern="pattern1" />
      </section>

      <section id="caracteristicas" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                Características Principales
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Galenos Web ofrece un conjunto completo de herramientas
                diseñadas específicamente para optimizar la gestión de tu
                práctica médica, mejorar la atención al paciente y simplificar
                los procesos administrativos.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => <motion.div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow" whileHover={{
              y: -5
            }}>
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>)}
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Salud Digital Integrada
              </h2>
              <p className="text-gray-600 mb-8">
                Galenos Web revoluciona la gestión médica con un sistema
                integral que conecta todos los aspectos de tu práctica médica en
                una sola plataforma, permitiéndote brindar una atención de
                calidad mientras optimizas tus procesos administrativos.
              </p>
              <ul className="space-y-4">
                {['Expedientes médicos digitales con firma electrónica', 'Sistema de citas inteligente con recordatorios automáticos', 'Facturación integrada con aseguradoras y particulares', 'Reportes personalizados por especialidad médica', 'Acceso móvil seguro para profesionales de la salud'].map((item, index) => <li key={index} className="flex items-center">
                    <Stethoscope className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Sistema Médico" className="rounded-lg shadow-2xl" />
              <div className="absolute inset-0 bg-gradient-to-tr from-red-600/20 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      <Wave color="#f9fafb" className="bg-white" pattern="pattern3" />
      <section id="precios" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Planes Personalizados</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Ofrecemos planes adaptados específicamente a las necesidades de
                tu institución médica. Contacta con nuestro equipo de ventas
                para recibir una propuesta personalizada.
              </p>
            </div>
            <div className="max-w-2xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center">
              <div className="mb-8">
                <motion.div initial={{
                scale: 0.8,
                opacity: 0
              }} whileInView={{
                scale: 1,
                opacity: 1
              }} transition={{
                duration: 0.5
              }} className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Building2 className="w-10 h-10 text-red-600" />
                </motion.div>
                <h3 className="text-2xl font-bold mb-4">
                  Solicita tu propuesta personalizada
                </h3>
                <p className="text-gray-600 mb-8">
                  Nuestro equipo de especialistas analizará las necesidades
                  específicas de tu institución médica para ofrecerte la mejor
                  solución adaptada a tu presupuesto y requerimientos.
                </p>
              </div>
              <Link to="/contact">
                <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría obtener más información sobre Galenos Web y sus planes personalizados para mi institución médica. ¿Podrían ayudarme a encontrar la solución ideal?" target="_blank" rel="noopener noreferrer" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-8 py-4 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium">
                  Contactar con ventas
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.a>
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern1" />
      <section id="soporte" className="py-20 bg-white">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Soporte Especializado</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Nuestro equipo de expertos en sistemas médicos está disponible
                para ayudarte a resolver cualquier duda o problema técnico que
                puedas tener con Galenos Web.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {supportOptions.map((option, index) => <motion.div key={index} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center" whileHover={{
              y: -5
            }}>
                  <div className="flex justify-center mb-4">{option.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
                  <p className="text-gray-600">{option.description}</p>
                </motion.div>)}
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    Capacitación médica continua
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Maximiza el uso de Galenos Web con nuestros programas de
                    capacitación diseñados específicamente para profesionales de
                    la salud y personal administrativo.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {['Sesiones de formación para todo tu equipo médico', 'Videos tutoriales por especialidad médica', 'Webinars mensuales sobre nuevas funcionalidades', 'Certificación para administradores del sistema'].map((item, i) => <li key={i} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría obtener más información sobre la capacitación de Galenos Web, ¿Podrían ayudarme a encontrar la solución ideal?" target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="inline-flex items-center px-6 py-3 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium">
                    Programar capacitación
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </motion.a>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1551076805-e1869033e561?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Capacitación médica" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1579684385127-1ef15d508118?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Consulta médica" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1581056771107-24ca5f033842?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Expediente digital" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1530497610245-94d3c16cda28?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Equipo médico" className="w-full h-48 object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="#f9fafb" className="bg-white" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">
            ¿Listo para transformar tu práctica médica?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Únete a cientos de profesionales médicos que ya optimizaron su
            gestión con Galenos Web. Prueba gratis durante 30 días sin
            compromiso.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría obtener más información sobre la capacitación de Galenos Web, ¿Podrían ayudarme a encontrar la solución ideal?" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="bg-red-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-red-700 transition-colors flex items-center justify-center">
              <Laptop className="w-5 h-5 mr-2" />
              Solicitar Demo Personalizada
            </motion.a>
          </div>
        </div>
      </AnimatedSection>
    </ProductLayout>;
};